import { InlineNotification, Tab, TabList, TabPanel, TabPanels, Tabs } from '@carbon/react';
import { Renew } from '@carbon/icons-react';
import {
  asProblem,
  DateRangeFilter,
  hasPermission,
  PageHeader,
  problemMessage,
  StatusTag,
  useAdmin,
  useFilters,
  useFormat,
  useReferenceOptions,
} from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { dayRange, setOnly, shortId } from './dates';
import { MultiChoice } from './fields';
import { LogDetail } from './LogDetail';
import { LogTable, type LogQuery } from './LogTable';

type Received = HoComponents['schemas']['ReceivedMessageDto'];
type Sent = HoComponents['schemas']['SentChangeDto'];
type Conflict = HoComponents['schemas']['ConflictDto'];
type Publication = HoComponents['schemas']['PublicationDto'];

// The ingest statuses as the shared StatusTag colours them: applied is done, queued and processing are in progress,
// failed (still retrying) and stopped (no more retries) are both failures.
const STATUS_TONE: Record<string, string> = {
  processed: 'approved',
  received: 'pending',
  processing: 'open',
  failed: 'failed',
  dead: 'declined',
};
const INGEST_STATUSES = ['received', 'processing', 'processed', 'failed', 'dead'];
const SCOPES = ['tenant', 'brand', 'store'];
const logsKey = ['ho', 'sync-logs'] as const;

function useFacets() {
  return useQuery({
    queryKey: [...logsKey, 'facets'],
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/sync/logs/facets');
      if (!data) throw error;
      return data;
    },
  });
}

function useStoreName() {
  const stores = useReferenceOptions('org.store');
  return {
    choices: (stores.data ?? []).map((s) => ({ id: s.value, label: s.label })),
    name: (id: string | null | undefined) =>
      id ? (stores.data?.find((s) => s.value === id)?.label ?? shortId(id)) : '',
  };
}

const asChoices = (values: readonly string[] | undefined) => (values ?? []).map((v) => ({ id: v, label: v }));

/**
 * What went between HO and the stores (ADR 0014–0016): messages stores pushed, changes HO sent, store changes that
 * clashed, and POS profiles synced. Failed or dead store messages can be queued again.
 */
export function SyncLogsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const tabs = [t('logs.sync.received'), t('logs.sync.sent'), t('logs.sync.conflicts'), t('logs.sync.publications')];
  return (
    <section className="pos-admin-page">
      <PageHeader title={t('logs.sync.title')} description={t('logs.sync.hint')} />
      <Tabs selectedIndex={tab} onChange={({ selectedIndex }) => setTab(selectedIndex)}>
        <TabList aria-label={t('logs.sync.title')} contained>
          {tabs.map((label) => (
            <Tab key={label}>{label}</Tab>
          ))}
        </TabList>
        <TabPanels>
          <TabPanel>{tab === 0 && <ReceivedLog />}</TabPanel>
          <TabPanel>{tab === 1 && <SentLog />}</TabPanel>
          <TabPanel>{tab === 2 && <ConflictLog />}</TabPanel>
          <TabPanel>{tab === 3 && <PublicationLog />}</TabPanel>
        </TabPanels>
      </Tabs>
    </section>
  );
}

function ReceivedLog() {
  const { t } = useTranslation();
  const format = useFormat();
  const { session } = useAdmin();
  const facets = useFacets();
  const stores = useStoreName();
  const queryClient = useQueryClient();
  const filters = useFilters({ store: '', status: '', type: '', table: '', from: '', to: '' });
  const [open, setOpen] = useState<Received>();
  const [retried, setRetried] = useState<number>();
  const statusName = (s: string) => t(`logs.sync.statuses.${s}` as I18nKey, { defaultValue: s });

  const retry = useMutation({
    mutationFn: async (messageIds: string[]) => {
      const { data, error } = await hoApi.POST('/api/v1/sync/logs/received/retry', { body: { messageIds } });
      if (!data) throw error;
      return data;
    },
    onSuccess: (result) => {
      setRetried(Number(result.queued));
      void queryClient.invalidateQueries({ queryKey: logsKey });
    },
  });

  const load = async (q: LogQuery) => {
    const { store, status, type, table, from, to } = filters.applied;
    const { data, error } = await hoApi.GET('/api/v1/sync/logs/received', {
      params: {
        query: { ...setOnly({ storeId: store, status, type, entityType: table, ...dayRange(from, to) }), ...q },
      },
    });
    if (!data) throw error;
    return data;
  };

  return (
    <>
      {retried !== undefined && (
        <InlineNotification
          kind="success"
          lowContrast
          title={t('logs.sync.retried', { count: retried })}
          onClose={() => setRetried(undefined)}
        />
      )}
      {retry.error && (
        <InlineNotification
          kind="error"
          role="alert"
          lowContrast
          hideCloseButton
          title={problemMessage(t, asProblem(retry.error))}
        />
      )}
      <LogTable<Received>
        label={t('logs.sync.received')}
        queryKey={[...logsKey, 'received']}
        load={load}
        rowId={(m) => m.messageId}
        filters={filters}
        onOpen={setOpen}
        batchAction={
          hasPermission(session, 'ho.sync.manage')
            ? { label: (count) => t('logs.sync.retry', { count }), icon: Renew, run: (ids) => retry.mutate(ids) }
            : undefined
        }
        filterFields={
          <>
            <MultiChoice
              id="received-store"
              label={t('logs.columns.store')}
              choices={stores.choices}
              value={filters.draft.store}
              onChange={(v) => filters.set('store', v)}
            />
            <MultiChoice
              id="received-status"
              label={t('logs.columns.status')}
              choices={INGEST_STATUSES.map((s) => ({ id: s, label: statusName(s) }))}
              value={filters.draft.status}
              onChange={(v) => filters.set('status', v)}
            />
            <MultiChoice
              id="received-type"
              label={t('logs.columns.messageType')}
              choices={asChoices(facets.data?.messageTypes)}
              value={filters.draft.type}
              onChange={(v) => filters.set('type', v)}
            />
            <MultiChoice
              id="received-table"
              label={t('logs.columns.table')}
              choices={asChoices(facets.data?.aggregateTypes)}
              value={filters.draft.table}
              onChange={(v) => filters.set('table', v)}
            />
            <DateRangeFilter
              id="received-date"
              legendText={t('logs.columns.received')}
              from={filters.draft.from}
              to={filters.draft.to}
              onChange={(r) => filters.setDraft((d) => ({ ...d, ...r }))}
            />
          </>
        }
        columns={[
          { key: 'time', header: t('logs.columns.received'), render: (m) => format.dateTime(m.receivedAt) },
          { key: 'store', header: t('logs.columns.store'), render: (m) => stores.name(m.storeId) },
          { key: 'type', header: t('logs.columns.messageType'), render: (m) => m.messageType },
          {
            key: 'record',
            header: t('logs.columns.record'),
            render: (m) => (
              <span className="pos-log__id">{`${m.aggregateType} ${shortId(m.aggregateId)} v${m.aggregateVersion}`}</span>
            ),
          },
          {
            key: 'status',
            header: t('logs.columns.status'),
            render: (m) => (
              <>
                <StatusTag status={STATUS_TONE[m.status] ?? m.status}>{statusName(m.status)}</StatusTag>
                {/* The full text is in the details that open with the row. */}
                {m.lastError && <p className="pos-log__error">{m.lastError}</p>}
              </>
            ),
          },
          {
            key: 'attempts',
            header: t('logs.columns.attempts'),
            render: (m) => <span className="pos-log__number">{m.attempts}</span>,
          },
        ]}
      />
      {open && (
        <LogDetail
          title={`${open.aggregateType} ${shortId(open.aggregateId)}`}
          label={t('logs.sync.received')}
          queryKey={[...logsKey, 'received', open.messageId]}
          loadPayload={async () => {
            const { data, error } = await hoApi.GET('/api/v1/sync/logs/received/{messageId}', {
              params: { path: { messageId: open.messageId } },
            });
            if (!data) throw error;
            return data.payload;
          }}
          onClose={() => setOpen(undefined)}
          fields={[
            [t('logs.columns.message'), open.messageId],
            [t('logs.columns.store'), stores.name(open.storeId)],
            [t('logs.columns.messageType'), open.messageType],
            [t('logs.columns.record'), `${open.aggregateType} ${open.aggregateId} v${open.aggregateVersion}`],
            [t('logs.columns.status'), statusName(open.status)],
            [t('logs.columns.attempts'), String(open.attempts)],
            [t('logs.columns.error'), open.lastError],
            [t('logs.columns.received'), format.dateTime(open.receivedAt)],
            [t('logs.columns.processed'), open.processedAt ? format.dateTime(open.processedAt) : null],
            [t('logs.columns.nextAttempt'), open.nextAttemptAt ? format.dateTime(open.nextAttemptAt) : null],
          ]}
        />
      )}
    </>
  );
}

function SentLog() {
  const { t } = useTranslation();
  const format = useFormat();
  const facets = useFacets();
  const stores = useStoreName();
  const filters = useFilters({ scope: '', store: '', table: '', from: '', to: '' });
  const [open, setOpen] = useState<Sent>();
  const scopeName = (c: Sent) =>
    c.scope === 'store'
      ? stores.name(c.storeId)
      : t(`logs.sync.scopes.${c.scope}` as I18nKey, { defaultValue: c.scope });

  const load = async (q: LogQuery) => {
    const { scope, store, table, from, to } = filters.applied;
    const { data, error } = await hoApi.GET('/api/v1/sync/logs/sent', {
      params: { query: { ...setOnly({ scope, storeId: store, entityType: table, ...dayRange(from, to) }), ...q } },
    });
    if (!data) throw error;
    return data;
  };

  return (
    <>
      <LogTable<Sent>
        label={t('logs.sync.sent')}
        queryKey={[...logsKey, 'sent']}
        load={load}
        rowId={(c) => String(c.sequence)}
        filters={filters}
        onOpen={setOpen}
        filterFields={
          <>
            <MultiChoice
              id="sent-scope"
              label={t('logs.columns.sentTo')}
              choices={SCOPES.map((s) => ({ id: s, label: t(`logs.sync.scopes.${s}` as I18nKey) }))}
              value={filters.draft.scope}
              onChange={(v) => filters.set('scope', v)}
            />
            <MultiChoice
              id="sent-store"
              label={t('logs.columns.store')}
              choices={stores.choices}
              value={filters.draft.store}
              onChange={(v) => filters.set('store', v)}
            />
            <MultiChoice
              id="sent-table"
              label={t('logs.columns.table')}
              choices={asChoices(facets.data?.entityTypes)}
              value={filters.draft.table}
              onChange={(v) => filters.set('table', v)}
            />
            <DateRangeFilter
              id="sent-date"
              legendText={t('logs.columns.time')}
              from={filters.draft.from}
              to={filters.draft.to}
              onChange={(r) => filters.setDraft((d) => ({ ...d, ...r }))}
            />
          </>
        }
        columns={[
          {
            key: 'sequence',
            header: t('logs.columns.sequence'),
            render: (c) => <span className="pos-log__number">{c.sequence}</span>,
          },
          { key: 'time', header: t('logs.columns.time'), render: (c) => format.dateTime(c.createdAt) },
          {
            key: 'record',
            header: t('logs.columns.record'),
            render: (c) => (
              <span className="pos-log__id">{`${c.entityType} ${shortId(c.entityId)} v${c.entityVersion}`}</span>
            ),
          },
          { key: 'sentTo', header: t('logs.columns.sentTo'), render: scopeName },
        ]}
      />
      {open && (
        <LogDetail
          title={`${open.entityType} ${shortId(open.entityId)}`}
          label={t('logs.sync.sent')}
          queryKey={[...logsKey, 'sent', open.sequence]}
          loadPayload={async () => {
            const { data, error } = await hoApi.GET('/api/v1/sync/logs/sent/{sequence}', {
              params: { path: { sequence: Number(open.sequence) } },
            });
            if (!data) throw error;
            return data.payload;
          }}
          onClose={() => setOpen(undefined)}
          fields={[
            [t('logs.columns.sequence'), String(open.sequence)],
            [t('logs.columns.time'), format.dateTime(open.createdAt)],
            [t('logs.columns.record'), `${open.entityType} ${open.entityId} v${open.entityVersion}`],
            [t('logs.columns.sentTo'), scopeName(open)],
          ]}
        />
      )}
    </>
  );
}

function ConflictLog() {
  const { t } = useTranslation();
  const format = useFormat();
  const facets = useFacets();
  const stores = useStoreName();
  const filters = useFilters({ store: '', resolution: '', table: '', from: '', to: '' });
  const [open, setOpen] = useState<Conflict>();
  const resolutionName = (r: string) => t(`logs.sync.resolutions.${r}` as I18nKey, { defaultValue: r });

  const load = async (q: LogQuery) => {
    const { store, resolution, table, from, to } = filters.applied;
    const { data, error } = await hoApi.GET('/api/v1/sync/logs/conflicts', {
      params: { query: { ...setOnly({ storeId: store, resolution, entityType: table, ...dayRange(from, to) }), ...q } },
    });
    if (!data) throw error;
    return data;
  };

  return (
    <>
      <LogTable<Conflict>
        label={t('logs.sync.conflicts')}
        queryKey={[...logsKey, 'conflicts']}
        load={load}
        rowId={(c) => c.id}
        filters={filters}
        onOpen={setOpen}
        filterFields={
          <>
            <MultiChoice
              id="conflict-store"
              label={t('logs.columns.store')}
              choices={stores.choices}
              value={filters.draft.store}
              onChange={(v) => filters.set('store', v)}
            />
            <MultiChoice
              id="conflict-resolution"
              label={t('logs.columns.resolution')}
              choices={(facets.data?.resolutions ?? []).map((r) => ({ id: r, label: resolutionName(r) }))}
              value={filters.draft.resolution}
              onChange={(v) => filters.set('resolution', v)}
            />
            <MultiChoice
              id="conflict-table"
              label={t('logs.columns.table')}
              choices={asChoices(facets.data?.aggregateTypes)}
              value={filters.draft.table}
              onChange={(v) => filters.set('table', v)}
            />
            <DateRangeFilter
              id="conflict-date"
              legendText={t('logs.columns.time')}
              from={filters.draft.from}
              to={filters.draft.to}
              onChange={(r) => filters.setDraft((d) => ({ ...d, ...r }))}
            />
          </>
        }
        columns={[
          { key: 'time', header: t('logs.columns.time'), render: (c) => format.dateTime(c.createdAt) },
          { key: 'store', header: t('logs.columns.store'), render: (c) => stores.name(c.storeId) },
          {
            key: 'record',
            header: t('logs.columns.record'),
            render: (c) => <span className="pos-log__id">{`${c.entityType} ${shortId(c.entityId)}`}</span>,
          },
          {
            key: 'versions',
            header: t('logs.columns.versions'),
            render: (c) => (
              <span className="pos-log__number">
                {t('logs.sync.versions', { incoming: c.incomingVersion ?? '—', current: c.currentVersion ?? '—' })}
              </span>
            ),
          },
          { key: 'resolution', header: t('logs.columns.resolution'), render: (c) => resolutionName(c.resolution) },
        ]}
      />
      {open && (
        <LogDetail
          title={`${open.entityType} ${shortId(open.entityId)}`}
          label={t('logs.sync.conflicts')}
          queryKey={[...logsKey, 'conflicts', open.id]}
          loadPayload={async () => {
            const { data, error } = await hoApi.GET('/api/v1/sync/logs/conflicts/{id}', {
              params: { path: { id: open.id } },
            });
            if (!data) throw error;
            return data.payload;
          }}
          onClose={() => setOpen(undefined)}
          fields={[
            [t('logs.columns.time'), format.dateTime(open.createdAt)],
            [t('logs.columns.store'), stores.name(open.storeId)],
            [t('logs.columns.record'), `${open.entityType} ${open.entityId}`],
            [
              t('logs.columns.versions'),
              t('logs.sync.versions', { incoming: open.incomingVersion ?? '—', current: open.currentVersion ?? '—' }),
            ],
            [t('logs.columns.resolution'), resolutionName(open.resolution)],
          ]}
        />
      )}
    </>
  );
}

function PublicationLog() {
  const { t } = useTranslation();
  const format = useFormat();
  const stores = useStoreName();
  const filters = useFilters({ store: '', from: '', to: '' });

  const load = async (q: LogQuery) => {
    const { store, from, to } = filters.applied;
    const { data, error } = await hoApi.GET('/api/v1/sync/logs/publications', {
      params: { query: { ...setOnly({ storeId: store, ...dayRange(from, to) }), ...q } },
    });
    if (!data) throw error;
    return data;
  };

  return (
    <LogTable<Publication>
      label={t('logs.sync.publications')}
      queryKey={[...logsKey, 'publications']}
      load={load}
      rowId={(p) => p.id}
      filters={filters}
      filterFields={
        <>
          <MultiChoice
            id="publication-store"
            label={t('logs.columns.store')}
            choices={stores.choices}
            value={filters.draft.store}
            onChange={(v) => filters.set('store', v)}
          />
          <DateRangeFilter
            id="publication-date"
            legendText={t('logs.columns.time')}
            from={filters.draft.from}
            to={filters.draft.to}
            onChange={(r) => filters.setDraft((d) => ({ ...d, ...r }))}
          />
        </>
      }
      columns={[
        { key: 'time', header: t('logs.columns.time'), render: (p) => format.dateTime(p.publishedAt) },
        {
          key: 'profile',
          header: t('logs.columns.profile'),
          render: (p) => (p.profileCode ? `${p.profileCode} · ${p.profileName ?? ''}` : shortId(p.profileId)),
        },
        { key: 'store', header: t('logs.columns.store'), render: (p) => stores.name(p.storeId) },
        { key: 'by', header: t('logs.columns.by'), render: (p) => p.publishedByName ?? '—' },
        {
          key: 'rows',
          header: t('logs.columns.rows'),
          render: (p) => <span className="pos-log__number">{p.rowCount}</span>,
        },
        {
          key: 'applied',
          header: t('logs.columns.status'),
          render: (p) => (
            <StatusTag status={p.applied ? 'active' : 'pending'}>
              {p.applied ? t('posSync.status.current') : t('posSync.status.pending')}
            </StatusTag>
          ),
        },
      ]}
    />
  );
}
